/* eslint-disable @typescript-eslint/restrict-template-expressions */
import '../../../components/JobQueue/JobQueue.scss';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Modal, Typography } from '@mui/material';
import dividerLogo from 'assets/images/split-v.svg';
import MoreCandidate from 'components/jobDescription/MoreCandidate';
import { CandidateSubmissionType, JobData } from 'components/JobQueue/utils';
import { Loader } from 'components/loader';
import React, { useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { getAllListData, ReplaceData } from 'services/CommonServices';
import { MASTER_ENDPOINT, SALES_ENDPOINT } from 'utils/Constant';
import { formatPhoneNumber } from 'utils/Helpers';

import JobDescription from '../../../components/jobDescription/JobDescription';
import MatchesAndShortlist from '../../../components/jobDescription/MatchesAndShortlist';
// import NoMatchesFound from '../../../components/jobDescription/NoMatchesFound';
import { downloadEmailWithAttachments } from '../downloadEml';
import EmlViewerFromApi from '../EmlViewer';

const modalBoxStyles = {
  alignItems: 'center',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.14)',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  left: '50%',
  maxWidth: '500px',
  padding: '32px 32px 28px',
  position: 'absolute' as const,
  textAlign: 'center' as const,
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100vw - 48px)',
};

const DownloadEmailPromptModal: React.FC<{
  open: boolean;
  onClose: () => void;
  type: string;
}> = ({ open, onClose, type }) => (
  <Modal
    open={open}
    onClose={onClose}
    aria-labelledby="download-email-modal-title"
    aria-describedby="download-email-modal-description">
    <Box sx={modalBoxStyles}>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: '#f4f5f7',
          borderRadius: '50%',
          color: '#222',
          display: 'flex',
          height: 64,
          justifyContent: 'center',
          width: 64,
        }}>
        {type === 'email' ? (
          <AccessTimeIcon sx={{ fontSize: 32 }} />
        ) : (
          <CheckCircleIcon sx={{ fontSize: 32 }} />
        )}
      </Box>
      <Typography
        id="download-email-modal-title"
        sx={{
          color: '#131722',
          fontFamily: 'Inter, sans-serif',
          fontSize: '18px',
          fontWeight: 600,
          lineHeight: '26px',
        }}>
        {type === 'email'
          ? 'Review and Send the Downloaded Email'
          : 'Submission Complete'}
      </Typography>
      <Typography
        id="download-email-modal-description"
        sx={{
          color: '#4b5563',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
        }}>
        {type === 'email'
          ? `Once the email has been downloaded, please open the file, review the
        details, and send it to the client associated with this record. Ensure
        all attachments and content are accurate before sending.`
          : 'The selected candidates have been successfully marked as submitted. They will now appear in the Submitted to Client list'}
      </Typography>
      <Button
        onClick={onClose}
        sx={{
          '&:hover': {
            backgroundColor: '#111827',
          },
          backgroundColor: '#1f2937',
          borderRadius: '8px',
          color: '#ffffff',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '20px',
          paddingX: '28px',
          paddingY: '10px',
          textTransform: 'none',
        }}>
        OK
      </Button>
    </Box>
  </Modal>
);

const JobDescriptionPage: React.FC = () => {
  const location = useLocation();
  const Params = location?.state?.Params;
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const [matches, setMatches] = useState<CandidateSubmissionType[]>();
  const [matchCount, setMatchCount] = React.useState(0);
  const [shortlist, setShortlist] = useState<CandidateSubmissionType[]>();
  const [shortlisted, setShortlisted] = useState<CandidateSubmissionType[]>([]);
  const [jobData, setJobData] = useState<JobData>({} as JobData);
  const [loader, setLoader] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState(0);
  const [more, setMore] = useState<boolean>(false);
  const [submittedRefreshSignal, setSubmittedRefreshSignal] = useState(0);
  // Initialize scoreFilter with score from URL params or Params (for Job Queue filter)
  const [scoreFilter, setScoreFilter] = useState<string>(
    searchParams.get('score') || Params?.score || '',
  );
  const [showDownloadEmailPrompt, setShowDownloadEmailPrompt] = useState<{
    open: boolean;
    type: string;
  }>({
    open: false,
    type: '',
  });
  const [emailData, setEmailData] = useState<string>('');

  const handleSelectCandidate = (candidateId: string, selected: boolean) => {
    // In Shortlisted tab: toggle selection only
    setShortlisted((prev: any[]) =>
      prev.map((c: any) =>
        c.id === candidateId ? { ...c, selectedForSubmit: selected } : c,
      ),
    );
  };

  const handleExpandCandidate = (candidateId: string) => {
    // Handle candidate expansion
    // console.log('Expand candidate:', candidateId);
  };

  const handleScoreFilterChange = (score: string) => {
    setScoreFilter(score);
  };

  const getMatchedAndShortlistedCandidates = async (id: string) => {
    // setLoader(true);
    const scoreParam = scoreFilter ? `&score=${scoreFilter}` : '';
    await getAllListData(
      `${
        SALES_ENDPOINT.MatchedCandidates
      }/${id}?type=matched&skip=0&take=5${scoreParam}&sourceQueue=${'benchQueue'}`,
    )
      .then((res) => {
        setMatches(res?.data || []);
        setMatchCount(res?.total || 0);
        // setMatches(SampleData);
        // setLoader(false);
      })
      .catch((err: any) => {})
      .finally(() => {});

    await getAllListData(
      `${
        SALES_ENDPOINT.MatchedCandidates
      }/${id}?type=shortlisted${scoreParam}&sourceQueue=${'benchQueue'}`,
    )
      .then((res) => {
        setShortlist(res?.data || []);
        // setMatches(SampleData);
        // setLoader(false);
      })
      .catch((err: any) => {})
      .finally(() => {});
  };
  const getJobData = async (id: string) => {
    setLoader(true);
    await getAllListData(
      `${MASTER_ENDPOINT.Jobs}?id=${id}&sourceQueue=${'benchQueue'}`,
    )
      .then((res) => {
        setJobData(res?.data?.[0]);
        setLoader(false);
      })
      .catch((err: any) => {})
      .finally(() => {
        setLoader(false);
      });
  };

  React.useEffect(() => {
    getJobData(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  React.useEffect(() => {
    getMatchedAndShortlistedCandidates(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, scoreFilter]);

  const formatEmailField = (value?: string | string[] | null): string => {
    if (!value) {
      return '';
    }
    if (Array.isArray(value)) {
      return value
        .map((entry) => entry?.trim())
        .filter((entry): entry is string => Boolean(entry))
        .join(', ');
    }
    return value.trim();
  };

  const generateCandidateSignature = (
    candidate: CandidateSubmissionType,
  ): string => {
    const c = candidate?.candidate;
    if (!c) return '';

    // Helper to escape HTML entities
    const escapeHtml = (text: any): string => {
      if (text === null || text === undefined || text === '') return '-';
      const str = String(text).trim();
      if (!str || str === '-') return '-';
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    // Helper to safely get field value (no escaping, for URLs)
    const getField = (value: any, fallback = '-'): string => {
      if (value === null || value === undefined || value === '')
        return fallback;
      return String(value).trim() || fallback;
    };

    // Build name
    const firstName = getField(c.firstName, '');
    const middleName = getField(c.middleName, '');
    const lastName = getField(c.lastName, '');
    const fullNameRaw = [firstName, middleName, lastName]
      .filter(Boolean)
      .join(' ');
    const fullName = escapeHtml(fullNameRaw || '-');

    // Build location
    const city = getField(c.city, '');
    const state = getField(c.state, '');
    const country = getField(c.country, '');
    const locationParts = [city, state, country].filter(Boolean);
    const locationRaw =
      locationParts.length > 0 ? locationParts.join(', ') : '-';
    const location = escapeHtml(locationRaw);

    // Build contact (phone)
    const phone = c.phone || c.mobileNumber || c.workPhone || '';
    const formattedPhone = phone ? formatPhoneNumber(phone) : '-';
    const contact = escapeHtml(formattedPhone);

    // Build email - validate it's a proper email before creating mailto link
    const emailRaw = getField(c.email, '');
    const isValidEmail =
      emailRaw &&
      emailRaw !== '-' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailRaw);
    const emailDisplay = escapeHtml(emailRaw);
    const emailLink = isValidEmail
      ? `<a href="mailto:${encodeURIComponent(
          emailRaw,
        )}" style="color: #0262b9; text-decoration: underline;">${emailDisplay}</a>`
      : emailDisplay;

    // Build visa status
    const visaStatusRaw = getField(c.workStatus?.name || c.workAuth, '-');
    const visaStatus = escapeHtml(visaStatusRaw);

    // Build availability
    // const availabilityRaw = getField(c.benchStatus || c.benchAvailability, '-');
    // const availability = escapeHtml(availabilityRaw);

    // Build LinkedIn
    const linkedinUrlRaw =
      (c as any).linkedInUrl || (c as any).linkedInUrl || '';
    let linkedin = '-';
    if (linkedinUrlRaw && linkedinUrlRaw.trim() && linkedinUrlRaw !== '-') {
      const linkedinUrl = getField(linkedinUrlRaw, '');
      let fullUrl = '';
      if (linkedinUrl.includes('http://') || linkedinUrl.includes('https://')) {
        fullUrl = linkedinUrl;
      } else {
        // URL encode the LinkedIn profile identifier
        const profileId = encodeURIComponent(
          linkedinUrl.replace(/^\/+|\/+$/g, ''),
        );
        fullUrl = `https://www.linkedin.com/in/${profileId}`;
      }
      const escapedUrl = escapeHtml(linkedinUrl);
      // Escape HTML entities in URL for href attribute
      const escapedFullUrl = fullUrl
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      linkedin = `<a href="${escapedFullUrl}" style="color: #0077b5; text-decoration: underline;">${escapedUrl}</a>`;
    }

    // <div style="margin-bottom: 2px;"><strong>Availability:</strong> ${availability}</div> */

    // Build experience - try to get from various possible fields
    const experience = (c as any)?.overallExperience || '-';

    // Build the signature HTML - simple list format matching the design
    const signature = `
      <div style="margin-top: 20px; padding-top: 16px; font-family: Arial, sans-serif; font-size: 12px; color: #111827; line-height: 1.8;">
        <div style="font-weight: 600; font-size: 14px; color: #111827; margin-bottom: 12px;">${fullName}</div>
        <div style="color: #111827;">
          <div style="margin-bottom: 2px;"><strong>Location:</strong> ${location}</div>
          <div style="margin-bottom: 2px;"><strong>Experience:</strong> ${
            experience
              ? experience === 1
                ? experience + ' year'
                : experience + ' years'
              : '-'
          }</div>
          <div style="margin-bottom: 2px;"><strong>Contact:</strong> ${contact}</div>
          <div style="margin-bottom: 2px;"><strong>E-Mail:</strong> ${emailLink}</div>
          <div style="margin-bottom: 2px;"><strong>Visa Status:</strong> ${visaStatus}</div>
          <div style="margin-bottom: 2px;"><strong>LinkedIn:</strong> ${linkedin}</div>
        </div>
      </div>
    `;

    return signature;
  };

  const generateCandidateSignatures = (
    candidates: CandidateSubmissionType[],
  ): string => {
    if (!candidates || candidates.length === 0) return '';

    const signatures = candidates
      .map((candidate) => generateCandidateSignature(candidate))
      .filter(Boolean)
      .join('');

    return signatures;
  };

  const handleConfirmSubmit = async (
    selected: CandidateSubmissionType[],
    type: string,
  ) => {
    const payload = {
      candidateId: selected.map((c) => c?.candidate?.id),
      isDownloadable:
        type === 'submit' ? false : type === 'email' ? true : false,
      sourceQueue: 'benchQueue',
    };
    // Call API to submit candidates
    await ReplaceData(
      '',
      payload,
      `${SALES_ENDPOINT.MatchedCandidates}/submit/${jobId}`,
      false,
    )
      .then(async (res) => {
        if (res?.zipUrl) {
          try {
            const fromAddress = formatEmailField(jobData?.emailMeta?.from);
            const ccAddress = formatEmailField(jobData?.emailMeta?.cc);
            const subjectLine = formatEmailField(jobData?.emailMeta?.subject);
            const getFileNameFromUrl = (urlString: string): string => {
              try {
                const { pathname } = new URL(urlString);
                const segment = pathname.substring(
                  pathname.lastIndexOf('/') + 1,
                );
                return decodeURIComponent(segment || 'attachment.zip');
              } catch (error) {
                console.warn(
                  'Unable to derive filename from URL',
                  urlString,
                  error,
                );
                return 'attachment.zip';
              }
            };

            // Prepend email body from EmlViewerFromApi if available
            const emailBodyPrefix = emailData
              ? `<div>${emailData}</div>\r\n`
              : '';

            // Generate candidate signatures
            const candidateSignatures = generateCandidateSignatures(selected);

            // Get and escape job title
            const jobTitle = jobData?.jobTitle || 'role';
            const escapedJobTitle = jobTitle
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');

            const EMAIL_BODY_HTML = [
              '<html><body style="font-family: Arial, sans-serif; font-size: 14px; color: #374151; line-height: 1.6;">',
              '<p>Hello,</p>',
              '<p>',
              'Please find attached the list of shortlisted candidates who closely align ',
              `with your requirements in terms of skills, experience, and project fit for your ${
                escapedJobTitle ? escapedJobTitle : ''
              } role. `,
              'Each profile has been reviewed to ensure relevance to the role and ',
              'suitability for your current needs.',
              '</p>',
              '<p>',
              'Kindly review the attached profiles and share your feedback or next steps ',
              "at your convenience. We'll be happy to coordinate interviews or provide ",
              'any additional information you may need.',
              '</p>',
              emailData
                ? '<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 20px 0;">'
                : '',
              candidateSignatures
                ? '<div style="margin-top: 24px;">' +
                  candidateSignatures +
                  '</div>'
                : '',
              emailData ? emailBodyPrefix : '',

              '</body></html>',
            ].join('\r\n');
            const formatMessageId = (id: string) => `<${id}@itechindia.co>`;
            const fallbackFileName = getFileNameFromUrl(res?.zipUrl);
            await downloadEmailWithAttachments({
              attachmentUrl: res?.zipUrl,
              cc: ccAddress,
              emailBodyHtml: EMAIL_BODY_HTML,
              fallbackFileName,
              from: jobData?.recruiterEmail
                ? jobData?.recruiterEmail
                : jobData?.emailMeta?.from,
              jobData: jobData,
              jobId: formatMessageId(
                `reply-${jobData?.emailMeta?.jobId}-${Date.now()}`,
              ),
              messageId: formatMessageId(jobData?.emailMeta?.messageId),
              references: [formatMessageId(jobData?.emailMeta?.messageId)],
              subject: subjectLine,
              to: fromAddress,
            });

            // await downloadEmailWithAttachment(res?.zipUrl);
            setShowDownloadEmailPrompt({
              open: true,
              type: type,
            });
          } catch (error) {
            console.error('Failed to build EML with attachment', error);
          }
        } else {
          setShowDownloadEmailPrompt({
            open: true,
            type: type,
          });
        }
        setSubmittedRefreshSignal((prev) => prev + 1);
      })
      .catch((err) => {
        // console.log(err);
      })
      .finally(() => {
        if (jobId) {
          getMatchedAndShortlistedCandidates(jobId);
        }
      });
  };

  return (
    <div
      className="inter-font-family"
      style={{
        backgroundColor: '#ffffff',
        height: 'calc(100vh - 70px)',
      }}>
      {loader ? (
        <Loader newLoader />
      ) : more ? (
        <MoreCandidate
          goBack={() => setMore(false)}
          jobId={jobId}
          score={scoreFilter}
          goal={jobData?.goal}
          jobTitle={jobData?.jobTitle}
          expFrom={jobData?.expFrom}
          expTo={jobData?.expTo}
        />
      ) : (
        <PanelGroup direction="horizontal" id="job-desc-group">
          <Panel defaultSize={70} minSize={35}>
            <div
              style={{
                borderRight: '1px solid #d9d9d9',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
              }}>
              <JobDescription
                jobData={jobData}
                jobSummary={jobData?.jobDescription}
                submittedRefreshSignal={submittedRefreshSignal}
              />
            </div>
          </Panel>

          <PanelResizeHandle className="panel-resize-handle">
            <img src={dividerLogo} className="handle-bar" alt="resize" />
          </PanelResizeHandle>

          <Panel defaultSize={30} minSize={35}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
              }}>
              {/* {jobData?.candidateMatchStatus === 'noMatches' &&
              (!matches || matches.length === 0) ? (
                <NoMatchesFound />
              ) : ( */}
              <MatchesAndShortlist
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                matches={matches}
                matchCount={matchCount}
                shortlist={shortlist}
                shortlisted={shortlisted}
                onSelectCandidate={handleSelectCandidate}
                onExpandCandidate={handleExpandCandidate}
                onConfirmSubmit={handleConfirmSubmit}
                setMore={() => setMore(true)}
                jobId={jobId}
                jobData={jobData}
                Params={Params}
                onScoreFilterChange={handleScoreFilterChange}
                scoreFilterValue={scoreFilter}
                goal={jobData?.goal}
              />
              {/* )} */}
            </div>
          </Panel>
        </PanelGroup>
      )}
      <DownloadEmailPromptModal
        open={showDownloadEmailPrompt.open}
        type={showDownloadEmailPrompt.type}
        onClose={() =>
          setShowDownloadEmailPrompt({
            open: false,
            type: '',
          })
        }
      />
      {/* Hidden component to fetch email data */}
      <div style={{ display: 'none' }}>
        <EmlViewerFromApi
          jobId={jobData?.id}
          setEmailDataContent={setEmailData}
          source={jobData?.jobSource}
        />
      </div>
    </div>
  );
};

export default JobDescriptionPage;
